<template>
  <div>
    <!-- Import Modals -->
    <TemplateImportMethodModal
      v-model:visible="displayImportMethodSelectionModal"
      @selected-import-method="handleSelectedImportMethod"
    />
    <ImportTemplateModal
      v-model:visible="displayImportTemplateModal"
    />
    <ImportModal
      v-if="showImportModal"
      :title="t('templates.import.header')"
      import-type="template"
      @close="handleCloseImportModal"
    />

    <!-- Template Selector for creation -->
    <TemplateSelectorV2
      v-if="showTemplateSelector"
      type-templates="none"
      origin-call="myTemplates"
      :id-campaign="null"
      :duplicate="true"
      @on-choose-template="selectTemplate"
      @on-close-dialog="closeTemplateSelector"
    />

    <!-- Action Components -->
    <RenameAction
      ref="renameTemplateRef"
      :dialog-title="t('templates.confirm.rename.title')"
      :input-label="t('templates.confirm.rename.message')"
      :cancel-label="t('close')"
      :confirm-label="t('save')"
      @confirm="onRenameConfirm"
    />

    <DuplicateAction
      ref="duplicateTemplateRef"
      :dialog-title="t('templates.confirm.duplicate.title')"
      :input-label="t('templates.confirm.duplicate.message')"
      :cancel-label="t('close')"
      :confirm-label="t('save')"
      @confirm="onDuplicateConfirm"
    />

    <DeleteAction
      ref="deleteTemplateRef"
      :dialog-title="t('templates.confirm.delete.title')"
      :message="t('templates.confirm.delete.message')"
      :cancel-label="t('close')"
      :confirm-label="t('save')"
      @confirm="onDeleteConfirm"
    />

    <div class="templates-page">
      <PageHeader
        :title="t('myStoreMenu.templates')"
        :summary="t('templates.summary')"
      >
        <template #actions>
          <SpmButton
            :label="t('templateSelector.import.importTemplateBtn')"
            icon="fa-regular fa-file-import"
            class-style="p-button p-button-secondary"
            @click="openImportMethodModal"
          />
          <SpmButton
            :label="t('templates.create')"
            icon="far fa-plus-circle"
            class-style="p-button p-button-primary"
            @click="showTemplateSelectorHandler"
          />
        </template>
      </PageHeader>
    </div>

    <div class="page-content">
      <div class="templates">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :show-reload-button="true"
          :columns="columns"
          data-key="id"
          :initial-sort="{ field: 'updated_at', order: 'desc' }"
          :expandable="true"
          :is-row-expandable="isRowExpandable"
          :row-actions-options="rowActions"
          :grouped-actions-options="groupedActions"
          :persistent-filters="persistentFilters"
          :exportable="true"
          :export-config="{ entityType: 'template' }"
          :enable-grouped-actions="true"
          :show-export-all-button="false"
          @export="handleExport"
        >
          <template #cell-label="{ data }">
            <div class="name-cell">
              <div class="name-row font-bold">
                <i
                  class="fa-regular fa-magnifying-glass mr-2"
                  style="cursor: pointer;"
                  @click="preview($event, data.id, data.type)"
                />
                <IntegrationOwnerBadge
                  v-if="data.owner_integration"
                  :owner-integration="data.owner_integration"
                />
                <span
                  v-if="data.name"
                  class="contact-name"
                >
                  {{ data.name }}
                </span>
                <span
                  v-else
                  class="cell-empty"
                >
                  -
                </span>
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <template #cell-type="{ data }">
            <div class="type-cell">
              <SpmBadge
                v-if="cleanStr(data.type)"
                :label="getTypeLabel(data.type)"
                :icon="getTypeIcon(data.type)"
                :colors="getTypeColors(data.type)"
              />
              <SpmBadge
                v-if="data.type === 'whatsapp' && data.whatsapp_status"
                :label="getWhatsappStatusLabel(data.whatsapp_status)"
                :icon="getWhatsappStatusIcon(data.whatsapp_status)"
                :colors="getWhatsappStatusColors(data.whatsapp_status)"
                size="sm"
              />
              <span
                v-if="!cleanStr(data.type)"
                class="cell-empty"
              >-</span>
            </div>
          </template>

          <template #empty>
            <SpmEmptyState
              icon="fa-regular fa-file-lines"
              :title="t('templates.emptyState.title')"
              :description="t('templates.emptyState.description')"
              :button-label="t('templates.create')"
              button-icon="far fa-plus-circle"
              @action="showTemplateSelectorHandler"
            />
          </template>

          <template #expansion="{ data }">
            <div class="expansion-content">
              <h4>{{ t('templates.funnels.title') }}</h4>
              <FunnelsListV2
                :template-config="data.config || '{}'"
                @edit-funnel="(funnelId, funnelName, funnelType) => handleEditFunnel(
                  {
                    idTemplate: data.id,
                    type: data.type,
                    name: data.name
                  }, funnelId, funnelName, funnelType)"
              />
            </div>
          </template>
        </SpmTableAPI>
      </div>
    </div>

    <TemplateHtmlPreviewSlider
      v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
      :previews="htmlPreviewData.previews"
      @close="closeHtmlPreview"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import FunnelsListV2 from '@/components/template-lists/FunnelsListV2.vue';
import {
  getChannelIcon as getChannelIconFromTokens,
  getChannelTagStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator, ExportPayload } from '@/components/table-rest-api/types';
import { isDemoModeActive, demoCampaigns } from '@/composables/DemoMode';
import { NestApiType, nestCall } from '@/composables/nestApi';
import RenameAction from '@/components/actions/RenameAction.vue';
import DuplicateAction from '@/components/actions/DuplicateAction.vue';
import DeleteAction from '@/components/actions/DeleteAction.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { RenameActionResult } from '@/components/actions/types';
import { useTemplatesApi } from '@/composables/templates/useTemplatesApi';
import { UserState } from '@/composables/User';
import { isWorkshopAccount } from '@/composables/WorkshopMode';
import type { GroupedActionOption, SelectionInfo } from '@/components/table-rest-api/types';
import { activate as activateTemplate, setRefreshTemplatesList, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { store } from '@/store';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import ImportTemplateModal from '@/components/template-lists/ImportTemplateModal.vue';
import TemplateImportMethodModal from '@/components/template-lists/TemplateImportMethodModal.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import { TEMPLATE_IMPORT_METHODS } from '@/configs/templates';
import { TemplateTypeEnum } from '@/composables/shop/Templates';

export default defineComponent({
  name: 'Templates',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
    IntegrationOwnerBadge,
    FunnelsListV2,
    RenameAction,
    DuplicateAction,
    DeleteAction,
    SpmButton,
    SpmEmptyState,
    TemplateSelectorV2,
    ImportTemplateModal,
    TemplateImportMethodModal,
    ImportModal,
    TemplateHtmlPreviewSlider,
  },

  setup() {
    const { t } = useI18n();
    const childComponentRef = ref();
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    // Action component refs
    const renameTemplateRef = ref();
    const duplicateTemplateRef = ref();
    const deleteTemplateRef = ref();

    // Import modals state
    const displayImportMethodSelectionModal = ref(false);
    const displayImportTemplateModal = ref(false);
    const showImportModal = ref(false);

    // Template selector state
    const showTemplateSelector = ref(false);

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (childComponentRef.value) {
        childComponentRef.value.reloadData();
      }
    };

    /**
     * Show active template editor
     */
    const showActiveTemplateEditor = async (template: { id: number; name: string; type: string }) => {
      if (template.type) {
        store.commit('general/showTheSpinner');
        activateTemplate(template.id, template.name, template.type)
          .then(() => {
            store.commit('general/hideTheSpinner');
          })
          .catch((error) => {
            console.error('Error activating template:', error);
            store.commit('general/hideTheSpinner');
          });
      }
    };

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/templates',
      secure: true,
    });

    // Persistent filters for templates + demo mode filter
    const persistentFilters = computed<SpmApiFilter[]>(() => {
      const filters: SpmApiFilter[] = [
        {
          field: 'created_from_campaign',
          operator: SpmFilterOperator.IS_NULL,
          values: '',
        },
        {
          field: 'id_campaign',
          operator: SpmFilterOperator.IS_NULL,
          values: '',
        },
        {
          field: 'deleted',
          operator: SpmFilterOperator.EQUALS,
          values: false,
        },
        {
          field: 'config',
          operator: SpmFilterOperator.JSON_NOT_EXISTS,
          values: 'origin_template_id',
        },
        {
          field: 'type',
          operator: SpmFilterOperator.NOT_EQUALS,
          values: TemplateTypeEnum.TEASER,
        },
      ];

      if (isDemoModeActive.value) {
        const ids = demoCampaigns.templateIds;
        filters.push({
          field: 'id',
          operator: SpmFilterOperator.IN,
          values: (ids.length > 0 ? ids.join(',') : '0') as any,
        });
      }

      return filters;
    });

    const columns: SpmColumnConfig[] = [
      // Hidden: ID for selection/actions
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      // Composite: fullname with firstname, lastname, created_at, updated_at
      {
        id: 'label',
        header: t('templates.name'),
        fields: [{
          name: 'name',
          label: t('templates.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('templates.date_add'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('templates.date_modif'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      // Single field columns
      {
        id: 'type',
        header: t('templates.type'),
        fields: [{
          name: 'type',
          label: t('templates.type'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'additional_data',
        header: '',
        fields: ['config'],
        hidden: true,
      },
    ];

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    /**
     * Get type icon using styleTokens
     */
    const getTypeIcon = (type: string): string => getChannelIconFromTokens(type) || 'fa-solid fa-question';

    /**
     * Get type colors for SpmBadge using styleTokens
     */
    const getTypeColors = (type: string): { bg: string; color: string; borderColor?: string } => {
      const vars = getChannelTagStyleVars(type);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    /**
     * Get type label
     */
    const getTypeLabel = (type: string): string => {
      const labelMap: Record<string, string> = {
        email: 'Email',
        sms: 'SMS',
        whatsapp: 'WhatsApp',
        pushnotifications: 'Push',
        popup: 'Popup',
        embed: 'Embed',
        facebookmessenger: 'Messenger',
        richtexting: 'RCS',
      };
      return labelMap[type.toLowerCase()] || type;
    };

    /**
     * Get WhatsApp status label
     */
    const getWhatsappStatusLabel = (status: string): string => {
      const key = `templates.whatsappStatus.${status}`;
      const translated = t(key);
      return translated !== key ? translated : status;
    };

    /**
     * Get WhatsApp status icon
     */
    const getWhatsappStatusIcon = (status: string): string => {
      const iconMap: Record<string, string> = {
        APPROVED: 'fa-solid fa-circle-check',
        REINSTATED: 'fa-solid fa-circle-check',
        PENDING: 'fa-solid fa-clock',
        IN_APPEAL: 'fa-solid fa-clock',
        REJECTED: 'fa-solid fa-circle-xmark',
        PAUSED: 'fa-solid fa-circle-pause',
        DISABLED: 'fa-solid fa-circle-minus',
        FLAGGED: 'fa-solid fa-flag',
        ARCHIVED: 'fa-solid fa-box-archive',
        DELETED: 'fa-solid fa-trash',
        PENDING_DELETION: 'fa-solid fa-trash',
        LIMIT_EXCEEDED: 'fa-solid fa-circle-exclamation',
        LOCKED: 'fa-solid fa-lock',
        ERROR: 'fa-solid fa-circle-exclamation',
      };
      return iconMap[status] || 'fa-solid fa-circle-question';
    };

    /**
     * Get WhatsApp status colors for SpmBadge
     */
    const getWhatsappStatusColors = (status: string): { bg: string; color: string } => {
      const sendable = ['APPROVED', 'REINSTATED'];
      const pending = ['PENDING', 'IN_APPEAL'];

      if (sendable.includes(status)) {
        return { bg: '#ecfdf5', color: '#059669' };
      }
      if (pending.includes(status)) {
        return { bg: '#fffbeb', color: '#d97706' };
      }
      return { bg: '#fef2f2', color: '#dc2626' };
    };

    /**
     * Check if a row should be expandable (has funnels)
     */
    const isRowExpandable = (row: any): boolean => {
      try {
        if (!row) return false;
        const { config } = row;
        if (!config) return false;
        return config && config.funnels && Array.isArray(config.funnels) && config.funnels.length > 0;
      } catch {
        return false;
      }
    };

    // Define row actions for templates
    const rowActions = [
      {
        label: t('templates.edit'),
        icon: 'far fa-edit',
        command: (rowData: any) => {
          showActiveTemplateEditor(rowData);
        },
      },
      {
        label: t('templates.confirm.rename.title'),
        icon: 'fa fa-pencil',
        command: (rowData: any) => {
          if (renameTemplateRef.value?.open) {
            renameTemplateRef.value.open({
              id: rowData.id,
              name: rowData.name,
            });
          }
        },
      },
      {
        label: t('templates.duplicate'),
        icon: 'fa fa-copy',
        command: (rowData: any) => {
          if (duplicateTemplateRef.value?.open) {
            duplicateTemplateRef.value.open({
              id: rowData.id,
              name: rowData.name,
            });
          }
        },
      },
      {
        label: t('templates.delete'),
        icon: 'fa fa-trash',
        command: (rowData: any) => {
          if (deleteTemplateRef.value?.open) {
            deleteTemplateRef.value.open({
              id: rowData.id,
              name: rowData.name,
            });
          }
        },
      },
    ];

    // Action confirm handlers
    const onRenameConfirm = async (result: RenameActionResult) => {
      try {
        const response = await nestCall(
          'patch',
          NestApiType.CLIENT_API,
          `/templates/${result.id}/rename`,
          {},
          '',
          { name: result.newName },
          true,
        );
        if (response.success) {
          showToastSuccess(t('templates.confirm.rename.success.message'));
          reloadTableData();
        } else {
          showToastError(t('templates.confirm.rename.error.message'));
        }
      } catch (error) {
        showToastError(t('templates.confirm.rename.error.message'));
      }
    };

    const { duplicateTemplate } = useTemplatesApi();

    const onDuplicateConfirm = async (result: RenameActionResult) => {
      try {
        const duplicateResult = await duplicateTemplate(result.id, {
          name: result.newName,
        });
        if (duplicateResult.success && duplicateResult.data?.id_template) {
          showToastSuccess(t('templates.confirm.duplicate.success.message'));
          reloadTableData();
        } else {
          showToastError(t('templates.confirm.duplicate.error.message'));
        }
      } catch (error) {
        showToastError(t('templates.confirm.duplicate.error.message'));
      }
    };

    const onDeleteConfirm = async (result: RenameActionResult) => {
      try {
        const response = await nestCall(
          'delete',
          NestApiType.CLIENT_API,
          `/templates/${result.id}`,
          {},
          '',
          {},
          true,
        );

        if (response.success) {
          showToastSuccess(t('templates.confirm.delete.success.message'));
          reloadTableData();
        } else {
          showToastError(t('templates.confirm.delete.error.message'));
        }
      } catch (error) {
        console.error('Delete template API error:', error);
        showToastError(t('templates.confirm.delete.error.message'));
      }
    };

    const handleEditFunnel = (rootTemplate: { idTemplate: number; type: string; name: string }, funnelId: number, funnelName: string, funnelType: string) => {
      store.commit('general/showTheSpinner');
      activateTemplate(funnelId, funnelName, funnelType, -1, true, rootTemplate)
        .then(() => {
          store.commit('general/hideTheSpinner');
        })
        .catch((error) => {
          console.error('Error activating template:', error);
          store.commit('general/hideTheSpinner');
        });
    };

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (childComponentRef.value?.unselectAll) {
        childComponentRef.value.unselectAll();
      }
    };

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);

        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        resetTableSelection();
        reloadTableData();
        setLoading(false);
      }
    };

    // ==========================================
    // Import handlers
    // ==========================================

    /**
     * Open import method selection modal
     */
    const openImportMethodModal = () => {
      displayImportMethodSelectionModal.value = true;
    };

    /**
     * Handle selected import method
     */
    const handleSelectedImportMethod = (importMethod: string) => {
      displayImportMethodSelectionModal.value = false;
      const { SPM_FILE, THIRD_PARTY } = TEMPLATE_IMPORT_METHODS;
      switch (importMethod) {
        case SPM_FILE:
          showImportModal.value = true;
          break;
        case THIRD_PARTY:
          displayImportTemplateModal.value = true;
          break;
        default:
          break;
      }
    };

    /**
     * Handle close import modal
     */
    const handleCloseImportModal = () => {
      showImportModal.value = false;
      reloadTableData();
    };

    // ==========================================
    // Template selector handlers
    // ==========================================

    /**
     * Show template selector for creation
     */
    const showTemplateSelectorHandler = () => {
      showTemplateSelector.value = true;
    };

    /**
     * Close template selector
     */
    const closeTemplateSelector = () => {
      showTemplateSelector.value = false;
    };

    /**
     * Select template from selector and activate editor
     */
    const selectTemplate = async (idTemplate: number, label: string, type: string) => {
      activateTemplate(idTemplate, label, type)
        .then(() => {
          showTemplateSelector.value = false;
          setRefreshTemplatesList(true);
          store.commit('general/hideTheSpinner');
        })
        .catch((error) => {
          console.error('Error activating template:', error);
          store.commit('general/hideTheSpinner');
        });
    };

    // Watch for template list refresh
    watch(() => TemplateEditorState.isTemplateEditorVisible, () => {
      if (!TemplateEditorState.isTemplateEditorVisible) {
        reloadTableData();
        setRefreshTemplatesList(false);
      }
    });

    /**
     * Preview template image
     */
    const preview = async (event: Event, idTemplate: number) => {
      if (event) {
        event.stopPropagation();
      }
      const targetElement = event.target as HTMLElement;
      targetElement.classList.remove('fa-magnifying-glass');
      targetElement.classList.add('fa-spinner', 'fa-spin');

      htmlPreviewData.value = null;
      try {
        const idTemplateB64 = btoa(String(idTemplate));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop: idShop.value, idTemplate: idTemplateB64 },
        );
        if (result?.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || 'Erreur lors du chargement de la prévisualisation');
        }
      } catch {
        showToastError('Erreur lors du chargement de la prévisualisation');
      }

      targetElement.classList.remove('fa-spinner', 'fa-spin');
      targetElement.classList.add('fa-magnifying-glass');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    // Workshop mode: publish selected templates to gallery
    const handlePublishToGallery = async (selection: SelectionInfo) => {
      const count = selection.selectionCount;
      if (count === 0) return;

      if (!confirm(t('templates.groupedActions.publishToGalleryConfirm', { count }))) {
        return;
      }

      try {
        setLoading(true);
        const response = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/data/publish-templates-to-gallery',
          {},
          '',
          {
            shopId: idShop.value,
            templateIds: selection.selectedRows.map(Number),
          },
          true,
        );

        if (response?.success) {
          await showToastSuccess(t('templates.groupedActions.publishToGallerySuccess'));
        } else {
          await showToastError(response?.message || t('templates.groupedActions.publishToGalleryError'));
        }
      } catch (error) {
        console.error('Publish templates to gallery error:', error);
        await showToastError(t('templates.groupedActions.publishToGalleryError'));
      } finally {
        resetTableSelection();
        setLoading(false);
      }
    };

    const groupedActions = computed<GroupedActionOption[]>(() => {
      const actions: GroupedActionOption[] = [];
      if (isWorkshopAccount.value) {
        actions.push({
          label: t('templates.groupedActions.publishToGallery'),
          icon: 'fa fa-cloud-arrow-up',
          command: (selection: SelectionInfo) => handlePublishToGallery(selection),
        });
      }
      return actions;
    });

    return {
      t,
      apiCall,
      columns,
      cleanStr,
      getTypeIcon,
      getTypeColors,
      getTypeLabel,
      getWhatsappStatusLabel,
      getWhatsappStatusIcon,
      getWhatsappStatusColors,
      isRowExpandable,
      rowActions,
      groupedActions,
      childComponentRef,
      renameTemplateRef,
      duplicateTemplateRef,
      deleteTemplateRef,
      onRenameConfirm,
      onDuplicateConfirm,
      onDeleteConfirm,
      handleEditFunnel,
      persistentFilters,
      handleExport,
      preview,
      htmlPreviewData,
      closeHtmlPreview,
      // Import modals
      displayImportMethodSelectionModal,
      displayImportTemplateModal,
      showImportModal,
      openImportMethodModal,
      handleSelectedImportMethod,
      handleCloseImportModal,
      // Template selector
      showTemplateSelector,
      showTemplateSelectorHandler,
      closeTemplateSelector,
      selectTemplate,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page layout */
.templates-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}


.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Page Content */
.page-content {
  flex: 1;
}

.name-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

.name-cell .date-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.name-cell .date-info i {
  font-size: 0.625rem;
  color: #9ca3af;
}

.type-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Type badges */
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;

  i {
    font-size: 0.75rem;
  }
}

.type-badge--email {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.type-badge--sms {
  background-color: #f0fdf4;
  color: #15803d;
}

.type-badge--whatsapp {
  background-color: #ecfdf5;
  color: #059669;
}

.type-badge--push {
  background-color: #fef3c7;
  color: #d97706;
}

.type-badge--popup {
  background-color: #fef3c7;
  color: #d97706;
}

.type-badge--embed {
  background-color: #f0fdf4;
  color: #15803d;
}

.type-badge--messenger {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.type-badge--richtexting {
  background-color: #ecfdf5;
  color: #059669;
}
</style>
